import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { FadeUpOnScroll, StaggerOnScroll, StaggerItem } from './ScrollReveal';
import { GradientText } from './AnimatedText';

const faqs = [
  {
    question: 'Brauche ich eine TSE für mein Kassensystem?',
    answer: 'Ja, seit dem 1. Januar 2020 ist eine TSE (Technische Sicherheitseinrichtung) für alle elektronischen Kassensysteme in Deutschland Pflicht. Cash-X enthält eine Cloud-TSE, die automatisch jede Transaktion signiert und speichert. Sie müssen sich um nichts kümmern – wir übernehmen das für Sie.'
  },
  {
    question: 'Wie lange dauert die Einrichtung?',
    answer: 'Die Grundeinrichtung dauert nur 5-10 Minuten. Sie erstellen ein Konto, fügen Ihre Produkte hinzu (oder nutzen unseren KI-Import per Foto), und können sofort loslegen. Unser Setup-Wizard führt Sie durch jeden Schritt.'
  },
  {
    question: 'Funktioniert Cash-X auch ohne Internet?',
    answer: 'Ja! Cash-X verfügt über einen vollständigen Offline-Modus. Sie können weiterhin kassieren, Bestellungen aufnehmen und Bons drucken. Sobald die Verbindung wiederhergestellt ist, werden alle Daten automatisch synchronisiert und TSE-signiert.'
  },
  {
    question: 'Welche Hardware benötige ich?',
    answer: 'Cash-X läuft auf jedem modernen Gerät mit Webbrowser – ob Tablet, PC oder iPad. Für den Bondruck unterstützen wir alle gängigen Thermodrucker (Epson, Star, etc.). Ein Kartenlesegerät können Sie optional von uns beziehen oder Ihr vorhandenes SumUp/Nexi verwenden.'
  },
  {
    question: 'Kann ich meine bestehende Speisekarte importieren?',
    answer: 'Absolut! Nutzen Sie unseren KI-Import: Fotografieren Sie einfach Ihre bestehende Speisekarte oder laden Sie ein PDF hoch. Unsere KI erkennt automatisch alle Gerichte mit Namen, Preisen und Kategorien. Das spart Stunden an manueller Eingabe.'
  },
  {
    question: 'Wie funktioniert die Integration mit Lieferando, Wolt & Co?',
    answer: 'Nach einmaliger Einrichtung (ca. 10 Minuten) werden alle Bestellungen von den Lieferportalen automatisch in Ihr Cash-X-System übertragen. Keine doppelte Eingabe, keine Fehler. Die Bestellungen erscheinen direkt auf Ihrem Küchendisplay und werden automatisch bearbeitet.'
  },
  {
    question: 'Was passiert bei einer Finanzamtsprüfung?',
    answer: 'Cash-X erstellt automatisch alle erforderlichen Exporte: DSFinV-K für das Finanzamt, DATEV für Ihren Steuerberater und TSE-Protokolle. Mit einem Klick generieren Sie alle benötigten Unterlagen. Unsere Kunden haben bisher jede Prüfung ohne Beanstandung bestanden.'
  },
  {
    question: 'Gibt es eine Mindestvertragslaufzeit?',
    answer: 'Nein! Sie können Cash-X monatlich kündigen. Wir glauben an unser Produkt und wollen Sie durch Qualität überzeugen, nicht durch lange Verträge. Bei jährlicher Zahlung sparen Sie zudem 20%.'
  }
];

export const FAQSectionUltimate = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-24 md:py-32 overflow-hidden"
      style={{ backgroundColor: '#080d18' }}
    >
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <FadeUpOnScroll className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-slate-500/10 border border-slate-500/30 rounded-full px-4 py-1.5 mb-6">
            <HelpCircle className="w-4 h-4 text-slate-400" />
            <span className="text-slate-400 text-sm font-medium">Häufig gestellte Fragen</span>
          </div>
          <h2 
            data-testid="faq-headline"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Noch <GradientText>Fragen</GradientText>?
          </h2>
          <p className="mt-6 text-lg sm:text-xl max-w-2xl mx-auto text-slate-400">
            Hier finden Sie Antworten auf die häufigsten Fragen zu Cash-X.
          </p>
        </FadeUpOnScroll>

        {/* FAQ Items */}
        <StaggerOnScroll className="space-y-4" staggerDelay={0.05}>
          {faqs.map((faq, index) => (
            <StaggerItem key={index}>
              <FAQItem
                question={faq.question}
                answer={faq.answer}
                isOpen={openIndex === index}
                onToggle={() => setOpenIndex(openIndex === index ? null : index)}
                testId={`faq-item-${index}`}
              />
            </StaggerItem>
          ))}
        </StaggerOnScroll>

        {/* Contact CTA */}
        <FadeUpOnScroll delay={0.3} className="mt-16 text-center">
          <p className="text-slate-400 mb-4">
            Ihre Frage ist nicht dabei?
          </p>
          <a
            href="mailto:support@cash-x.de"
            className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-medium transition-colors"
          >
            Kontaktieren Sie unseren Support
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </a>
        </FadeUpOnScroll>
      </div>
    </section>
  );
};

const FAQItem = ({ question, answer, isOpen, onToggle, testId }) => {
  return (
    <motion.div
      data-testid={testId}
      className={`border rounded-xl overflow-hidden transition-colors ${
        isOpen 
          ? 'bg-[#111C32] border-blue-500/30' 
          : 'bg-[#111C32]/60 border-slate-700/50 hover:border-slate-600'
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full px-6 py-5 flex items-center justify-between text-left"
        aria-expanded={isOpen}
      >
        <span className="font-medium text-white pr-4">{question}</span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="shrink-0"
        >
          <ChevronDown className={`w-5 h-5 ${isOpen ? 'text-blue-400' : 'text-slate-400'}`} />
        </motion.div>
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <div className="px-6 pb-5">
              <p className="text-slate-400 leading-relaxed">
                {answer}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default FAQSectionUltimate;
